/**
 * <s-hero-carousel> — Homepage hero slideshow (no dependencies).
 *
 * Progressive enhancement over a native scroll-snap track: without JS the
 * slides are a swipeable strip. With JS it adds autoplay, arrows, dots and a
 * pause/play button.
 *
 * Required child markup:
 *   [data-track]     — horizontal scroll-snap container holding the slides.
 *   [data-slide]     — one per slide (role="group", aria-roledescription="slide").
 *   [data-controls]  — hidden until this script runs.
 *   [data-prev] [data-next] [data-dot] [data-pause] — buttons.
 *
 * Element dataset:
 *   data-interval     — autoplay dwell per slide in ms (default 3000).
 *   data-label-pause / data-label-play — accessible names for the pause button.
 *
 * Behaviour:
 *   - Autoplay pauses while the pointer hovers the track, while focus is
 *     inside the track, while the tab is hidden and while the carousel is
 *     off screen. It never starts when prefers-reduced-motion is set.
 *   - Using arrows, dots or swiping stops autoplay (APG carousel pattern);
 *     the play button restarts it.
 *   - Non-current slides are inert and aria-hidden so hidden links are skipped by keyboard
 *     and assistive tech. The track's aria-live is "off" while playing and
 *     "polite" once stopped.
 *   - Lazy images on the next slide are switched to eager before it arrives.
 */
class SHeroCarousel extends HTMLElement {
  #track;
  #slides = [];
  #dots = [];
  #pauseBtn;
  #index = 0;
  #timer = 0;
  #interval = 3000;
  #playing = false;
  #hovered = false;
  #focused = false;
  #offscreen = false;
  #reduced;
  #ac;
  #slideObserver;
  #viewObserver;

  connectedCallback() {
    this.#track = this.querySelector('[data-track]');
    this.#slides = [...this.querySelectorAll('[data-slide]')];
    if (!this.#track || this.#slides.length < 2) return;

    this.#dots = [...this.querySelectorAll('[data-dot]')];
    this.#pauseBtn = this.querySelector('[data-pause]');
    this.#interval = Number(this.dataset.interval) || 3000;
    this.#reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    const controls = this.querySelector('[data-controls]');
    if (controls) controls.hidden = false;

    this.querySelector('[data-prev]')?.addEventListener('click', () => this.#manual(this.#index - 1), opts);
    this.querySelector('[data-next]')?.addEventListener('click', () => this.#manual(this.#index + 1), opts);
    this.#dots.forEach((dot, i) => dot.addEventListener('click', () => this.#manual(i), opts));
    this.#pauseBtn?.addEventListener('click', () => this.#setPlaying(!this.#playing), opts);

    this.#track.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') { this.#hovered = true; this.#schedule(); } }, opts);
    this.#track.addEventListener('pointerleave', (e) => { if (e.pointerType === 'mouse') { this.#hovered = false; this.#schedule(); } }, opts);
    this.#track.addEventListener('focusin', () => { this.#focused = true; this.#schedule(); }, opts);
    this.#track.addEventListener('focusout', (e) => {
      if (!this.#track.contains(e.relatedTarget)) { this.#focused = false; this.#schedule(); }
    }, opts);
    // A swipe is a manual navigation: stop autoplay.
    this.#track.addEventListener('touchstart', () => this.#setPlaying(false), { ...opts, passive: true });
    this.#track.addEventListener('wheel', (e) => { if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) this.#setPlaying(false); }, { ...opts, passive: true });

    document.addEventListener('visibilitychange', () => this.#schedule(), opts);
    this.#reduced.addEventListener('change', () => { if (this.#reduced.matches) this.#setPlaying(false); }, opts);

    this.#slideObserver = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.6) this.#setCurrent(this.#slides.indexOf(entry.target));
      }
    }, { root: this.#track, threshold: [0.6] });
    this.#slides.forEach((slide) => this.#slideObserver.observe(slide));

    this.#viewObserver = new IntersectionObserver(([entry]) => {
      this.#offscreen = !entry.isIntersecting;
      this.#schedule();
    });
    this.#viewObserver.observe(this);

    this.#setCurrent(0, true);
    this.#setPlaying(!this.#reduced.matches);
    this.dataset.ready = '';
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#slideObserver?.disconnect();
    this.#viewObserver?.disconnect();
    clearTimeout(this.#timer);
  }

  #wrap(i) {
    const n = this.#slides.length;
    return ((i % n) + n) % n;
  }

  #setCurrent(i, force = false) {
    if (i < 0 || (i === this.#index && !force)) return;
    this.#index = i;
    this.#slides.forEach((slide, n) => {
      slide.inert = n !== i;
      slide.toggleAttribute('aria-hidden', n !== i);
      if (n !== i) slide.setAttribute('aria-hidden', 'true');
    });
    this.dataset.current = String(i + 1);
    this.#dots.forEach((dot, n) => dot.setAttribute('aria-current', n === i ? 'true' : 'false'));
    this.#warm(this.#wrap(i + 1));
    this.#schedule();
  }

  #warm(i) {
    this.#slides[i]?.querySelectorAll('img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; });
  }

  #go(i) {
    const target = this.#slides[this.#wrap(i)];
    this.#warm(this.#wrap(i));
    const delta = target.getBoundingClientRect().left - this.#track.getBoundingClientRect().left;
    this.#track.scrollBy({ left: delta, behavior: this.#reduced.matches ? 'auto' : 'smooth' });
  }

  #manual(i) {
    this.#setPlaying(false);
    this.#go(i);
  }

  #setPlaying(on) {
    this.#playing = on;
    this.#track.setAttribute('aria-live', on ? 'off' : 'polite');
    if (this.#pauseBtn) {
      this.#pauseBtn.setAttribute('aria-label', on ? this.dataset.labelPause : this.dataset.labelPlay);
      const pauseIcon = this.#pauseBtn.querySelector('[data-icon-pause]');
      const playIcon = this.#pauseBtn.querySelector('[data-icon-play]');
      if (pauseIcon) pauseIcon.hidden = !on;
      if (playIcon) playIcon.hidden = on;
    }
    this.#schedule();
  }

  #schedule() {
    clearTimeout(this.#timer);
    if (!this.#playing || this.#hovered || this.#focused || this.#offscreen || document.hidden) return;
    this.#timer = setTimeout(() => this.#go(this.#index + 1), this.#interval);
  }
}

if (!customElements.get('s-hero-carousel')) customElements.define('s-hero-carousel', SHeroCarousel);
